/**
 * FeedbackPanel - Displays captured feedback in JSON and Markdown formats
 */

import React, { useState } from 'react';
import { useFeedback } from 'feedbacker-react';
import { Feedback } from 'feedbacker-react';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-markdown';

interface FeedbackPanelProps {
  className?: string;
  captureLibrary?: 'html2canvas' | 'snapdom';
}

export const FeedbackPanel: React.FC<FeedbackPanelProps> = ({ className, captureLibrary }) => {
  const [activeTab, setActiveTab] = useState<'json' | 'markdown'>('json');
  
  // Use the feedback hook properly
  const feedbackData = useFeedback();
  const feedbackList = feedbackData?.feedbacks || [];
  const clearAllFeedback = feedbackData?.clearAll;

  // No need for separate state - use feedbackList directly

  const formatJSON = (feedback: Feedback[]): string => {
    if (feedback.length === 0) {
      return '{\n  "feedback": [],\n  "message": "No feedback captured yet. Click on components in feedback mode to capture data."\n}';
    }
    return JSON.stringify(feedback, null, 2);
  };

  const formatMarkdown = (feedback: Feedback[]): string => {
    if (feedback.length === 0) {
      return '# No Feedback Captured\n\nActivate feedback mode and click on components to capture feedback.';
    }

    return feedback.map((item, index) => {
      const componentInfo = item.componentInfo;
      const browserInfo = item.browserInfo;
      
      return `## Feedback #${index + 1}

### Component Information
- **Component:** ${componentInfo?.name || 'Unknown'}
- **Path:** ${componentInfo?.path?.join(' > ') || 'N/A'}
- **Timestamp:** ${new Date(item.timestamp).toLocaleString()}

### Feedback
${item.comment || 'No comment provided'}

### Browser Information
- **URL:** ${browserInfo?.url || 'N/A'}
- **Viewport:** ${browserInfo?.viewport.width}x${browserInfo?.viewport.height}
- **User Agent:** ${browserInfo?.userAgent || 'N/A'}

${item.htmlSnippet ? `### HTML Snippet\n\`\`\`html\n${item.htmlSnippet}\n\`\`\`` : ''}

---
`;
    }).join('\n');
  };

  const handleClear = () => {
    if (clearAllFeedback) {
      clearAllFeedback();
    }
  };

  return (
    <div className={`feedback-panel ${className || ''}`}>
      <div className="feedback-panel-header">
        <div className="panel-header-top">
          <h3>Captured Feedback</h3>
          {captureLibrary && (
            <span className={`capture-library-badge ${captureLibrary}`}>
              {captureLibrary === 'snapdom' ? '⚡' : '📷'} {captureLibrary}
            </span>
          )}
        </div>
        <div className="feedback-panel-controls">
          <div className="tab-buttons">
            <button 
              className={`tab-btn ${activeTab === 'json' ? 'active' : ''}`}
              onClick={() => setActiveTab('json')}
            >
              JSON
            </button>
            <button 
              className={`tab-btn ${activeTab === 'markdown' ? 'active' : ''}`}
              onClick={() => setActiveTab('markdown')}
            >
              Markdown
            </button>
          </div>
          <button 
            className="clear-btn"
            onClick={handleClear}
            disabled={feedbackList.length === 0}
          >
            Clear
          </button>
        </div>
      </div>
      
      <div className="feedback-panel-content">
        {activeTab === 'json' ? (
          <pre className="code-display json-display language-json">
            <code 
              className="language-json"
              dangerouslySetInnerHTML={{ 
                __html: Prism.highlight(formatJSON(feedbackList), Prism.languages.json, 'json') 
              }} 
            />
          </pre>
        ) : (
          <pre className="code-display markdown-display language-markdown">
            <code 
              className="language-markdown"
              dangerouslySetInnerHTML={{ 
                __html: Prism.highlight(formatMarkdown(feedbackList), Prism.languages.markdown, 'markdown') 
              }} 
            />
          </pre>
        )}
      </div>

      {feedbackList.length > 0 && (
        <div className="feedback-panel-footer">
          <span className="feedback-count">
            {feedbackList.length} feedback item{feedbackList.length !== 1 ? 's' : ''} captured
          </span>
        </div>
      )}
    </div>
  );
};